import { CorrectionListService } from './../correction-list/correction-list.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';
import { SpellCheckDialogService } from './spell-check-dialog.service';
import { ISpellCheckResponse } from './spell-check.service';
import { ReadingAtom } from 'src/app/core/reading-utils/interfaces';
import { TxtItemType, ReadingUnitType } from 'src/app/core/reading-utils/enums';
import { ITxtmapItem, SpellCheckWordStatus, HtmlTextWalker, msspldCls } from 'src/app/core/html-and-text-utils';
import { SpellCheckApiService } from './spell-check-api.service';
import { environment } from 'src/environments/environment';
import { TabService } from '../tab.service';
import { DocumentManagerService } from '../document/document-manager.service';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { ToastrDialogService } from '../toastr-dialog.service';
import { ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';
import { HuginnErrorCodesEnum } from 'src/app/utils/enums/error-codes/huginn-error-codes.enum';
import { ISpellCheckLangugeStore, ISpellCheckWord, SpellCheckStorageService } from './spell-check-storage';

@Injectable({
  providedIn: 'root'
})
export class SpellCheckWordMgrService {

  private newTextToProcess: boolean = false;

  private processing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private get currentDoc(): AsgardDocHtml | AsgardDocFroala | null {
    const doc = this.docMgrService.currentDoc;
    if (doc && (doc instanceof AsgardDocHtml || doc instanceof AsgardDocFroala)) {
      return doc;
    }
    return null;
  }

  private buildWalker: HtmlTextWalker;

  get buildTextMap(): ITxtmapItem[] {
    if (this.buildWalker) {
      return this.buildWalker.textMap.filter(x => x.type === TxtItemType.Word);
    }
    return [];
  }

  get wordItemUnderCursor(): ReadingAtom | null {
    const wordItem = this.currentDoc !== null ? this.currentDoc.getItemUnderCursor(ReadingUnitType.Word) : null;
    return wordItem && wordItem.length ? wordItem[0] : null;
  }

  get currentLangCode(): string | null {
    return this.tabService.activeTab !== null ? this.tabService.getTabLanguage(this.tabService.activeTab.id) : null;
  }

  get currentLangStorage(): ISpellCheckLangugeStore | null {
    if (this.currentLangCode) {
      const r = this.spellCheckStorageService.getWordsForLang(this.currentLangCode);
      if (r) {
        return r;
      }
    }
    return null;
  }

  get documentIFrame(): HTMLIFrameElement | null {
    if (this.currentDoc instanceof AsgardDocFroala || this.docMgrService.isFocusDocument && this.currentDoc instanceof AsgardDocHtml) {
      return this.currentDoc.editorFrame;
    }
    return null;
  }

  get getWordIndexUnderCursor(): number | null {
    const item = this.wordItemUnderCursor;
    if (item) {
      return item.itemIdx;
    }
    return null;
  }

  get wrongWords(): ITxtmapItem[] {
    return this.buildTextMap.filter(i => {
      if (this.currentLangStorage) {
        return this.currentLangStorage.words.find(r => r.word === i.content && r.status === SpellCheckWordStatus.INVALID);
      }
    });
  }

  constructor(
    private readonly dialogSvc: SpellCheckDialogService,
    private readonly tabService: TabService,
    private readonly splchkrApi: SpellCheckApiService,
    private readonly docMgrService: DocumentManagerService,
    private readonly correctionListService: CorrectionListService,
    private readonly spellCheckStorageService: SpellCheckStorageService,
    private readonly ngZone: NgZone,
    private readonly toastrService: ToastrDialogService,
  ) {
    this.initSubscirptions();
  }

  placeCaretAfterNode(node: Node): void {
    if (!this.documentIFrame) {
      return;
    }

    const doc = this.documentIFrame.contentDocument;
    const empty = document.createTextNode('\u00A0');

    if (node && node.parentNode && doc) {
      node.parentNode.insertBefore(empty, node.nextSibling);
      const sel = doc.getSelection();
      const range = doc.createRange();
      range.selectNodeContents(empty);
      range.collapse(false);
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }

  initSpellCheck = () => {
    if (!this.currentDoc || !(environment.spellCheck.supportedLangCodes.find(i => i === this.currentLangCode))) {
      return;
    }

    if (!this.processing.value) {
      this.processText();
    } else {
      this.newTextToProcess = true;
    }
  }

  unmarkAllMisspelledWords(): void {
    this.updateWalker();

    this.wrongWords.forEach(x => {
      this.unMarkWord(x);
    });
  }


  hideDialogIfShowing(): void {
    if (this.dialogSvc.dialogActive) {
      this.dialogSvc.hideDialog();
    }
  }

  checkCurrentWordSpell(): boolean | null {
    let isCorrect: boolean | null = false;

    if (this.wordItemUnderCursor) {
      const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
      const isMapWordCorrect = txtMapItem && this.ifWordIncorrect(txtMapItem) === null;
      const storageWord = txtMapItem && this.getWordFromStorage(txtMapItem.content) || null;
      const text = this.wordItemUnderCursor.text;
      const isFromSuggestions = storageWord !== null && storageWord.suggestions ?
        (storageWord.suggestions.filter(x => x === text).length > 0) : false;

      isCorrect = isFromSuggestions || isMapWordCorrect;

      if (isCorrect && txtMapItem) {
        this.unMarkWord(txtMapItem);
      }
    }

    return isCorrect;
  }

  inMisspeledNode(): HTMLElement | undefined {
    if (this.documentIFrame) {
      const doc = this.documentIFrame.contentDocument;
      if (!doc) {
        return;
      }
      const sel = doc.getSelection();
      if (!sel) {
        return;
      }
      const node = sel.anchorNode;
      if (!node) {
        return;
      }
      if (node.nodeType === 3) {
        const parent = node.parentElement;
        if (parent && parent.className && parent.className.indexOf(msspldCls) > -1) {
          return parent;
        }
      } else {
        const elem = this.textNodeInSelection(sel);
        if (elem && elem.className && elem.className.indexOf(msspldCls) > -1) {
          return elem;
        }
      }
    }
    return;
  }

  private textNodeInSelection(selection: Selection): HTMLElement | null {
    const range = selection.getRangeAt(0);

    if (range) {
      const el = range.startContainer.childNodes[range.startOffset - 1];

      if (el && el.childNodes.length === 1 && el.firstChild && el.firstChild.nodeType === 3) {
        return el as HTMLElement;
      }
    }
    return null;
  }

  getProcessingObservable(): Observable<boolean> {
    return this.processing.asObservable();
  }

  private updateWalker(): void {
    const doc = this.currentDoc;

    if (!doc || !doc.editorElement || !doc.editorWin) {
      return;
    }

    this.buildWalker = new HtmlTextWalker(doc.editorElement, doc.editorWin.document);
  }

  private markWrongWords(words: ITxtmapItem[]): void {
    const offsetPrev = this.buildWalker.getSelectionOffset();
    let wasMarked = false;

    words.forEach((i) => {
      wasMarked = wasMarked || this.buildWalker.markMisspeledWord(i.itemIndex);
    });

    const offsetNew = this.buildWalker.getSelectionOffset();
    if (offsetPrev && offsetNew) {
      if (wasMarked && (offsetPrev.globalOffset + offsetPrev.localOffset !== offsetNew.globalOffset + offsetNew.localOffset)) {
        this.buildWalker.setSelectionOffset(offsetPrev.globalOffset, offsetPrev.localOffset);
      }
    }
  }

  private unMarkWord(word: ITxtmapItem): void {
    const incorrectWord = this.getWordFromMapByIndex(word.itemIndex);

    if (incorrectWord) {
      const nodeMisspeledWord = this.buildWalker.getMisspeledWordWrapper(incorrectWord.itemIndex);
      if (nodeMisspeledWord) {
        const offset = this.buildWalker.getSelectionOffset();
        const parent = nodeMisspeledWord.parentNode;
        const childNodes = Array.from(nodeMisspeledWord.childNodes);

        if (parent) {
          childNodes.forEach(x => {
            parent.insertBefore(x, nodeMisspeledWord);
          });
          parent.removeChild(nodeMisspeledWord);
        }

        if (offset) {
          this.buildWalker.setSelectionOffset(offset.globalOffset, offset.localOffset);
        }
      }
    }
  }

  private getWordFromStorage(word: string): ISpellCheckWord | undefined {
    if (this.currentLangStorage) {
      return this.currentLangStorage.words.find(i => i.word === word);
    }
  }

  private ifWordIncorrect(word: ITxtmapItem): ISpellCheckWord | undefined {
    if (this.currentLangStorage) {
      return this.currentLangStorage.words.find(i => i.word === word.content && i.status === SpellCheckWordStatus.INVALID);
    }
  }

  private getWordFromMapByIndex(idx: number | null): ITxtmapItem | null {
    if (idx === null || !this.buildTextMap || this.buildTextMap && this.buildTextMap.length === 0) {
      return null;
    }

    const result = this.buildTextMap.find(i => i.itemIndex === idx);

    if (result) {
      return result;
    }
    return null;
  }

  private wordSuggestions(word: ITxtmapItem): string[] {
    const item = this.getWordFromStorage(word.content);

    if (item) {
      return item.suggestions || [];
    }

    return [];
  }

  getMisspeledWordPos(): { x: number, y: number } | null {
    if (!this.currentDoc || !this.currentDoc.editorFrame) {
      return null;
    }

    const style = window.getComputedStyle(this.currentDoc.editorFrame);
    if (style) {
      const topmargin = parseInt(style.marginTop ? style.marginTop : '0', 10) || 0;
      const toppading = parseInt(style.paddingTop ? style.paddingTop : '0', 10) || 0;
      const leftMargin = parseInt(style.marginLeft ? style.marginLeft : '0', 10) || 0;
      const leftPadding = parseInt(style.paddingLeft ? style.paddingLeft : '0', 10) || 0;
      const boundary = this.currentDoc.editorFrame.getBoundingClientRect();
      const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
      if (txtMapItem) {
        const clickedNode = this.buildWalker.getMisspeledWordWrapper(txtMapItem.itemIndex);
        if (clickedNode) {
          const boundaryClickedNode = (clickedNode as HTMLElement).getBoundingClientRect();
          const arrowHeight = 5;
          const x = boundary.left + leftMargin + leftPadding + ((boundaryClickedNode.width / 2) + boundaryClickedNode.left);
          const y = boundary.top + toppading + topmargin + boundaryClickedNode.height + boundaryClickedNode.top + arrowHeight;
          return { x, y };
        }
      }
    }
    return null;
  }

  getCurrentWordSuggestions(): string[] | null {
    if (!this.currentDoc) {
      return null;
    }

    const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
    if (txtMapItem) {
      return this.wordSuggestions(txtMapItem);
    }

    return null;
  }

  private async processText(): Promise<void> {
    this.processing.next(true);
    this.updateWalker();

    // if we find same word set status as in storage
    this.setTextMapWordsStatusFromStorage(this.buildTextMap);

    // take processing words add it to sentence and send it to api
    let processingMap = this.buildTextMap.filter(i => i.status === SpellCheckWordStatus.PROCESSING || i.hasToBeRechecked);
    let addedWords = this.addWords(processingMap);

    if (addedWords && addedWords.length > 0) {
      while (addedWords && addedWords.length > 0) {
        const wordsToValidate = this.tryToReduceAmountOfWords(addedWords);

        try {
          await this.validateSentences(wordsToValidate);
        } catch (err) {
          if (err.errorCode === HuginnErrorCodesEnum.SpellCheckInputSentenceIsTooLong) {
            this.showErrorMessage(ToastrStringsEnum.SpellcheckWordIsLong);
          }

          wordsToValidate.forEach(x => x.status = SpellCheckWordStatus.UNPROCESSED);
        }

        this.findInvalidWordsAndMark();

        processingMap = this.buildTextMap.filter(i => i.status === SpellCheckWordStatus.PROCESSING || i.hasToBeRechecked);
        addedWords = this.addWords(processingMap);
      }
    }

    this.findInvalidWordsAndMark();
    this.findValidWordsAndUnmark();

    this.processing.next(false);

    if (this.newTextToProcess) {
      this.newTextToProcess = false;
      await this.processText();
    }
  }

  private tryToReduceAmountOfWords(txtMap: ITxtmapItem[]): ITxtmapItem[] {
    let length = 0;
    let idx = 0;

    for (; idx < txtMap.length; idx++) {
      if (length + txtMap[idx].txtSize >= 1000) {
        break;
      } else {
        length += txtMap[idx].txtSize;
      }
    }

    if (idx > 0 && idx < txtMap.length) {
      return txtMap.slice(0, idx);
    }

    return txtMap;
  }

  private async validateSentences(txtMap: ITxtmapItem[]): Promise<void> {
    const words = txtMap.map(x => x.content);
    if (this.currentLangCode) {
      const result = await this.splchkrApi.validateSentence(this.currentLangCode, words);
      if (result) {
        this.addResponseToStorage(result);
        this.setTextMapWordsStatusFromStorage(txtMap);
      }
    }
  }

  private findInvalidWordsAndMark(): void {
    const invalidWords = this.buildTextMap.filter(i => i.status === SpellCheckWordStatus.INVALID);

    const recheckedWords = invalidWords.filter(i => i.hasToBeRechecked);

    recheckedWords.forEach(i => {
      this.unMarkWord(i);
      i.hasToBeRechecked = undefined;
    });

    if (invalidWords && invalidWords.length) {
      this.markWrongWords(invalidWords);
    }
  }

  private setTextMapWordsStatusFromStorage(textMap: ITxtmapItem[]): void {
    textMap.forEach(item => {
      const word = this.getWordFromStorage(item.content);
      if (word) {
        item.status = word.status;
      }
    });

    // mark last word to recheck
    if (textMap.length > 0) {
      textMap[textMap.length - 1].hasToBeRechecked = true;
    }
  }

  private findValidWordsAndUnmark(): void {
    const validWords = this.buildTextMap.filter(i => i.status === SpellCheckWordStatus.VALID);

    if (validWords && validWords.length) {
      validWords.forEach(word => this.unMarkWord(word));
    }
  }

  private addWords(words: ITxtmapItem[]): ITxtmapItem[] {
    const notStoredWords: ITxtmapItem[] = [];
    words.forEach(word => {
      if (!this.getWordFromStorage(word.content)) {
        notStoredWords.push(word);
      }
    });

    return notStoredWords;
  }

  private addResponseToStorage(result: ISpellCheckResponse[]): void {
    result.forEach(item => {
      let word = this.getWordFromStorage(item.word);
      if (!word) {
        word = {
          word: item.word
        } as ISpellCheckWord;
      }
      if (item.correct) {
        word.status = SpellCheckWordStatus.VALID;
      } else {
        word.status = SpellCheckWordStatus.INVALID;
        word.suggestions = item.suggestions;
      }
      if (this.currentLangStorage) {
        this.currentLangStorage.words.push(word);
      }
    });
  }

  private initSubscirptions(): void {
    this.dialogSvc.replaceWordEv.subscribe((e: string) => {
      this.replaceWordToSuggested(e);
    });

    this.dialogSvc.ignoreWordEv.subscribe((e: boolean) => {
      if (e) {
        this.ignoreWord();
      }
    });
    this.dialogSvc.ignoreAllEv.subscribe((e: boolean) => {
      if (e) {
        this.ignoreAll();
      }
    });
    this.dialogSvc.addWordEv.subscribe((e: boolean) => {
      if (e) {
        this.rememberWord();
      }
    });
  }

  private replaceWordToSuggested(word: string): void {
    const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
    if (!txtMapItem) {
      this.dialogSvc.hideDialog();
      return;
    }
    const node = this.buildWalker.getMisspeledWordWrapper(txtMapItem.itemIndex) as HTMLElement;
    node.innerText = word;
    this.unMarkWord(txtMapItem);
    this.dialogSvc.hideDialog();

    if (this.currentDoc instanceof AsgardDocHtml) {
      this.currentDoc.onDocumentChanged();
      this.updateWalker();
    }
  }

  private ignoreWord(): void {
    const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
    if (txtMapItem) {
      this.unMarkWord(txtMapItem);
    }

    this.dialogSvc.hideDialog();
  }

  private ignoreAll(): void {
    const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
    if (txtMapItem && this.currentLangStorage) {
      this.currentLangStorage.words.forEach(w => {
        if (w.word === txtMapItem.content) {
          w.status = SpellCheckWordStatus.VALID;
          this.unMarkWord(txtMapItem);
        }
      });
      this.unmarkWordsInTextMap(txtMapItem.content);
    }
    this.dialogSvc.hideDialog();
  }

  private rememberWord(): void {
    const txtMapItem = this.getWordFromMapByIndex(this.getWordIndexUnderCursor);
    if (this.currentLangStorage) {
      this.currentLangStorage.words.forEach(w => {
        if (txtMapItem && w.word === txtMapItem.content) {
          w.status = SpellCheckWordStatus.VALID;
          this.unMarkWord(txtMapItem);
        }
      });
    }
    if (txtMapItem && this.currentLangCode) {
      this.correctionListService.addWordToList(txtMapItem.content, this.currentLangCode);
    }
    this.dialogSvc.hideDialog();
  }

  private showErrorMessage(type: ToastrStringsEnum): void {
    this.ngZone.run(() => {
      this.toastrService.showToastrByType(ToastrActionEnum.Error, type, false);
    });
  }

  private unmarkWordsInTextMap(word: string): void {
    if (this.buildWalker.textMap && this.buildWalker.textMap.length) {
      this.buildWalker.textMap.forEach(w => {
        if (w.content === word) {
          this.unMarkWord(w);
        }
      });
    }
  }
}
